import React, { useMemo, useRef, useState } from 'react'

import {
  ChevronDownIcon,
  ChevronRightIcon,
  GripVerticalIcon,
  ListChecksIcon,
} from 'lucide-react'
import {
  arrayMove,
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc'

import { PropertyType } from '~/client/graph/types.generated'
import { Button } from '~/client/src/shared/components/Button/Button'
import { CollapseButton } from '~/client/src/shared/components/CollapseButton/CollapseButton'
import { PanelSections } from '~/client/src/shared/components/PanelSections/PanelSections'
import SearchBar from '~/client/src/shared/components/SearchBar/SearchBar'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { RESOURCE_NAME_KEY } from '~/client/src/shared/resources/definitions/types'
import { getSystemFormFieldKeys } from '~/client/src/shared/resources/systemFormFields'
import { formatPropertyType } from '~/client/src/shared/utils/propertyTypeUtils'

import { ROOT_FIELD_META } from '../ResourceViewEdit/useResourceFields'
import type { FormPropertyEdit, PropertyData } from './formProperties'
import { sortFormProperties } from './formProperties'
import { SystemPropertyRow } from './SystemPropertyRow'

const FORM_NAME_MAX_LENGTH = 100

export interface IFormPropertiesShellSubmit {
  name: string
  properties: FormPropertyEdit[]
}

interface IProps {
  headerTitle: string
  submitLabel: string
  resourceTypeId: string
  properties: PropertyData[]
  loading: boolean
  saving: boolean
  initialName: string
  autoFocusName: boolean
  onClose: () => void
  onBackToPicker?: () => void
  onSubmit: (input: IFormPropertiesShellSubmit) => void
}

const DragHandle = SortableHandle(() => (
  <div
    onClick={(e: React.MouseEvent) => e.stopPropagation()}
    className="tw:flex tw:items-center tw:self-start tw:px-3 tw:h-[42px] tw:cursor-grab tw:text-foreground active:tw:cursor-grabbing"
  >
    <GripVerticalIcon size={16} />
  </div>
))

const SortableItem = SortableElement(
  ({
    prop,
    isExpanded,
    onToggleExpand,
    onRequiredToggle,
    onToggleHidden,
  }: {
    prop: PropertyData
    isExpanded: boolean
    onToggleExpand: (propertyId: string) => void
    onRequiredToggle: (propertyId: string) => void
    onToggleHidden: (propertyId: string) => void
  }) => {
    const isHidden = prop.isHidden
    return (
      <div className="tw:flex tw:select-none tw:z-[9999]">
        <DragHandle />

        <div className="tw:flex-1 tw:min-w-0 tw:flex tw:flex-col tw:border-0 tw:border-b tw:border-solid tw:border-border">
          <div
            data-testid="row-header"
            className={`tw:flex tw:items-center tw:px-4 tw:py-2 tw:gap-3 tw:min-h-[42px] tw:cursor-pointer ${
              isHidden ? 'tw:bg-muted/50' : 'tw:bg-muted'
            }`}
            onClick={() => onToggleExpand(prop.propertyId)}
          >
            <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              {/* A mandatory property cannot be deselected - the resource declares it as
                  part of what it is. Shown ticked and disabled rather than hidden, so the
                  row still reads as included. */}
              <Checkbox
                checked={!prop.isHidden}
                disabled={prop.isLocked}
                onCheckedChange={() => onToggleHidden(prop.propertyId)}
              />
            </div>
            {isExpanded ? (
              <ChevronDownIcon
                size={16}
                className={`tw:shrink-0 ${
                  isHidden
                    ? 'tw:text-muted-foreground'
                    : 'tw:text-muted-foreground'
                }`}
              />
            ) : (
              <ChevronRightIcon
                size={16}
                className={`tw:shrink-0 ${
                  isHidden
                    ? 'tw:text-muted-foreground'
                    : 'tw:text-muted-foreground'
                }`}
              />
            )}
            <div
              className={`tw:flex-1 tw:min-w-0 tw:break-words tw:text-label-lg ${
                isHidden ? 'tw:text-muted-foreground' : 'tw:text-foreground'
              }`}
            >
              {prop.label}
              {prop.required && (
                <span
                  data-testid="mandatory-asterisk"
                  className="tw:text-destructive tw:ml-1"
                >
                  *
                </span>
              )}
            </div>
          </div>
          {isExpanded && (
            <div className="tw:px-3 tw:bg-background">
              <div
                data-testid="property-detail-row"
                className="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:py-2"
              >
                <span className="tw:shrink-0 tw:text-label-lg tw:text-muted-foreground">
                  {Localization.translator.name}
                </span>
                <span className="tw:min-w-0 tw:break-words tw:text-right tw:text-body-md tw:text-foreground tw:font-mono">
                  {prop.name}
                </span>
              </div>
              <div
                data-testid="property-detail-row"
                className="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:py-2"
              >
                <span className="tw:shrink-0 tw:text-label-lg tw:text-muted-foreground">
                  {Localization.translator.type}
                </span>
                <span className="tw:min-w-0 tw:break-words tw:text-right tw:text-body-md tw:text-foreground">
                  {formatPropertyType(prop.type, prop.configuration)}
                </span>
              </div>
              <div
                data-testid="property-detail-row"
                className="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:py-2"
              >
                <span className="tw:min-w-0 tw:break-words tw:text-label-lg tw:text-muted-foreground">
                  {Localization.translator.mandatory}
                </span>
                {/* Locked rows are required by declaration, so the switch shows the
                    state and refuses to change it - mirroring the system rows, whose
                    switch is disabled for the same reason. */}
                <Switch
                  className="tw:shrink-0"
                  checked={prop.required}
                  disabled={prop.isLocked}
                  onCheckedChange={() => onRequiredToggle(prop.propertyId)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  },
)

const SortableList = SortableContainer(
  ({
    items,
    expandedRows,
    isDragDisabled,
    onToggleExpand,
    onRequiredToggle,
    onToggleHidden,
  }: {
    items: PropertyData[]
    expandedRows: Set<string>
    isDragDisabled: boolean
    onToggleExpand: (propertyId: string) => void
    onRequiredToggle: (propertyId: string) => void
    onToggleHidden: (propertyId: string) => void
  }) => (
    <div>
      {items.map((prop, index) => (
        <SortableItem
          key={prop.propertyId}
          index={index}
          prop={prop}
          isExpanded={expandedRows.has(prop.propertyId)}
          onToggleExpand={onToggleExpand}
          onRequiredToggle={onRequiredToggle}
          onToggleHidden={onToggleHidden}
          disabled={prop.isHidden || isDragDisabled}
        />
      ))}
    </div>
  ),
)

export const FormPropertiesShell: React.FC<IProps> = ({
  headerTitle,
  submitLabel,
  resourceTypeId,
  properties,
  loading,
  saving,
  initialName,
  autoFocusName,
  onClose,
  onBackToPicker,
  onSubmit,
}) => {
  const [formName, setFormName] = useState(initialName)
  const [editedProperties, setEditedProperties] = useState<PropertyData[]>(() =>
    sortFormProperties(properties),
  )
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const contentRef = useRef<HTMLDivElement>(null)

  const trimmedSearch = searchQuery.trim().toLowerCase()
  const isSearchActive = !!trimmedSearch

  const filteredProperties = useMemo(() => {
    if (!trimmedSearch) return editedProperties
    return editedProperties.filter(p =>
      (p.label || p.name || '').toLowerCase().includes(trimmedSearch),
    )
  }, [editedProperties, trimmedSearch])

  // Always-present, non-removable system fields: Name (required) plus the
  // resource's root columns (icon/color/parent) shown like Name but optional.
  const systemRows = useMemo(
    () =>
      getSystemFormFieldKeys(resourceTypeId)
        // Only render a root field we have presentation for.
        .filter(key => key === RESOURCE_NAME_KEY || ROOT_FIELD_META[key])
        .map(key => {
          if (key === RESOURCE_NAME_KEY) {
            return {
              key,
              label: Localization.translator.name,
              typeLabel: formatPropertyType(PropertyType.Text),
              required: true,
            }
          }

          const label = ROOT_FIELD_META[key].getLabel()
          return { key, label, typeLabel: label, required: false }
        }),
    [resourceTypeId],
  )

  const visibleSystemRows = systemRows.filter(
    row =>
      !isSearchActive ||
      row.label.toLowerCase().includes(trimmedSearch) ||
      row.key.toLowerCase().includes(trimmedSearch),
  )

  const handleRequiredToggle = (propertyId: string) => {
    setEditedProperties(prev =>
      prev.map(prop => {
        if (prop.propertyId !== propertyId) return prop
        // A locked property is required by declaration; the switch cannot turn it off.
        if (prop.isLocked) return prop
        const newRequired = !prop.required
        return {
          ...prop,
          required: newRequired,
          isHidden: prop.isHidden && newRequired ? false : prop.isHidden,
        }
      }),
    )
  }

  const handleToggleHidden = (propertyId: string) => {
    setEditedProperties(prev =>
      prev.map(prop => {
        if (prop.propertyId !== propertyId) return prop
        // Belt to the disabled checkbox's braces: the row cannot be deselected, so no
        // path through here may hide it or clear its required flag.
        if (prop.isLocked) return prop
        const newIsHidden = !prop.isHidden
        return {
          ...prop,
          isHidden: newIsHidden,
          required: newIsHidden ? false : prop.required,
        }
      }),
    )
  }

  const handleToggleExpand = (propertyId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId)
      } else {
        newSet.add(propertyId)
      }
      return newSet
    })
  }

  const handleCollapseAll = () => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      visibleSystemRows.forEach(row => next.delete(row.key))
      filteredProperties.forEach(p => next.delete(p.propertyId))
      return next
    })
  }

  const handleExpandAll = () => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      visibleSystemRows.forEach(row => next.add(row.key))
      filteredProperties.forEach(p => next.add(p.propertyId))
      return next
    })
  }

  // Locked rows are always selected and refuse to toggle, so they are display-only as far
  // as select-all is concerned. Counting them would let the toolbar checkbox render enabled
  // over a filter that holds nothing it can actually change.
  const selectableFilteredProperties = filteredProperties.filter(
    p => !p.isLocked,
  )
  const userRowsInFilter = selectableFilteredProperties.length
  const userVisibleCount = selectableFilteredProperties.filter(
    p => !p.isHidden,
  ).length
  const totalVisibleRows = filteredProperties.length + visibleSystemRows.length
  const allChecked = userVisibleCount === userRowsInFilter
  // System rows are always selected, so a mixed user selection reads as
  // indeterminate whenever any system row is shown.
  const isIndeterminate =
    visibleSystemRows.length > 0
      ? !allChecked
      : userVisibleCount > 0 && !allChecked
  // Toolbar tracks visible rows only: stale state for hidden rows must not
  // count toward the collapse-all decision.
  const hasVisibleExpandedRow =
    visibleSystemRows.some(row => expandedRows.has(row.key)) ||
    filteredProperties.some(p => expandedRows.has(p.propertyId))
  const isAllCollapsed = !hasVisibleExpandedRow
  // Counts what the user SEES ticked, so it includes locked rows - they render checked and
  // cannot be unchecked. userVisibleCount is deliberately not reused here: that one drives
  // select-all, which locked rows are excluded from, and borrowing it reported a checked
  // row as unselected.
  const displayedSelectedCount =
    filteredProperties.filter(p => !p.isHidden).length +
    visibleSystemRows.length

  const handleToggleAll = () => {
    const shouldHideAll = allChecked
    const filteredIds = new Set(
      selectableFilteredProperties.map(p => p.propertyId),
    )
    setEditedProperties(prev =>
      prev.map(p =>
        // filteredIds already excludes locked rows - they are not select-all's to move.
        filteredIds.has(p.propertyId)
          ? {
              ...p,
              isHidden: shouldHideAll,
              required: shouldHideAll ? false : p.required,
            }
          : p,
      ),
    )
  }

  const handleSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => {
    const oldProp = editedProperties[oldIndex]
    const visibleProps = editedProperties.filter(p => !p.isHidden)
    const hiddenProps = editedProperties.filter(p => p.isHidden)

    if (oldProp.isHidden) {
      const hiddenIndex = hiddenProps.findIndex(
        p => p.propertyId === oldProp.propertyId,
      )
      const newHiddenIndex = newIndex - visibleProps.length
      const reorderedHidden = arrayMove(
        hiddenProps,
        hiddenIndex,
        newHiddenIndex,
      )
      const updated = [...visibleProps, ...reorderedHidden].map(
        (prop, index) => ({
          ...prop,
          orderIndex: index,
        }),
      )
      setEditedProperties(updated)
    } else {
      const visibleIndex = visibleProps.findIndex(
        p => p.propertyId === oldProp.propertyId,
      )
      const reorderedVisible = arrayMove(visibleProps, visibleIndex, newIndex)
      const updated = [...reorderedVisible, ...hiddenProps].map(
        (prop, index) => ({
          ...prop,
          orderIndex: index,
        }),
      )
      setEditedProperties(updated)
    }
  }

  const buildPropertyEdits = (): FormPropertyEdit[] =>
    sortFormProperties(editedProperties).map(prop => ({
      propertyId: prop.propertyId,
      isRequired: prop.required,
      isHidden: prop.isHidden,
    }))

  const handleSubmit = () => {
    const trimmedName = formName.trim()
    if (!trimmedName) return
    onSubmit({ name: trimmedName, properties: buildPropertyEdits() })
  }

  const isSubmitDisabled = saving || loading || !formName.trim()

  return (
    <div className="tw:w-full tw:h-full tw:flex tw:flex-col tw:bg-background">
      <PanelSections.Header title={headerTitle} onClose={onClose} />
      <div className="tw:px-4 tw:border-0 tw:border-b tw:border-solid tw:border-border">
        <StruxhubInput
          label={Localization.translator.formNameLabel}
          value={formName}
          isRequired
          maxLength={FORM_NAME_MAX_LENGTH}
          placeholder={Localization.translator.untitled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormName(e.target.value)
          }
          autoFocus={autoFocusName}
        />
      </div>
      {!loading && editedProperties.length > 0 && (
        <div className="tw:px-4 tw:pt-4">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onReset={() => setSearchQuery('')}
            onTagClick={() => setSearchQuery('')}
            customPlaceholder={Localization.translator.searchProperties}
            useAlternativeLayout
            className="tw:h-[40px]"
          />
        </div>
      )}
      {!loading && totalVisibleRows > 0 && (
        <div
          data-testid="properties-action-bar"
          className="tw:flex tw:items-center tw:gap-3 tw:py-2 tw:pl-14 tw:pr-4 tw:border-0 tw:border-b tw:border-solid tw:border-border"
        >
          <Checkbox
            checked={allChecked}
            indeterminate={isIndeterminate}
            disabled={userRowsInFilter === 0}
            onCheckedChange={handleToggleAll}
          />
          <div data-testid="collapse-toggle" className="tw:flex">
            <CollapseButton
              isExpanded={!isAllCollapsed}
              onClick={isAllCollapsed ? handleExpandAll : handleCollapseAll}
              ariaLabel={
                isAllCollapsed
                  ? Localization.translator.expandAll
                  : Localization.translator.collapseAll
              }
            />
          </div>
          <div className="tw:text-label-lg tw:text-foreground">{`${displayedSelectedCount} ${Localization.translator.fieldsSelected}`}</div>
        </div>
      )}
      <div className="tw:flex-1 tw:overflow-y-auto" ref={contentRef}>
        {loading && (
          <div className="tw:p-6 tw:text-body-md tw:text-foreground">{`${Localization.translator.loading}...`}</div>
        )}
        {!loading && (
          <>
            {visibleSystemRows.map(row => (
              <SystemPropertyRow
                key={row.key}
                label={row.label}
                propertyKey={row.key}
                typeLabel={row.typeLabel}
                required={row.required}
                isExpanded={expandedRows.has(row.key)}
                onToggleExpand={() => handleToggleExpand(row.key)}
              />
            ))}
            {editedProperties.length === 0 && !isSearchActive && (
              <div className="tw:text-center tw:text-body-md tw:text-muted-foreground tw:px-5 tw:py-10">
                {Localization.translator.noPropertiesFoundForForm}
              </div>
            )}
            {isSearchActive && totalVisibleRows === 0 && (
              <div className="tw:text-center tw:text-body-md tw:text-muted-foreground tw:px-5 tw:py-10">
                {Localization.translator.noPropertiesMatchSearch}
              </div>
            )}
            {filteredProperties.length > 0 && (
              <SortableList
                items={filteredProperties}
                expandedRows={expandedRows}
                isDragDisabled={isSearchActive}
                onToggleExpand={handleToggleExpand}
                onRequiredToggle={handleRequiredToggle}
                onToggleHidden={handleToggleHidden}
                onSortEnd={handleSortEnd}
                useDragHandle={true}
                lockAxis="y"
                helperContainer={() => contentRef.current || document.body}
              />
            )}
          </>
        )}
      </div>
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:p-3 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:bg-background">
        {onBackToPicker ? (
          <Button
            variant="outline"
            size="lg"
            onClick={onBackToPicker}
            disabled={saving}
          >
            <ListChecksIcon data-icon="inline-start" size={16} />
            {Localization.translator.select}
          </Button>
        ) : (
          <div />
        )}
        <div className="tw:flex tw:items-center tw:gap-2">
          <Button
            variant="outline"
            size="lg"
            onClick={onClose}
            disabled={saving}
          >
            {Localization.translator.cancel}
          </Button>
          <Button
            variant="default"
            size="lg"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            {saving && (
              <span data-icon="inline-start" className="tw:inline-flex">
                <Spinner size="sm" variant="white" />
              </span>
            )}
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
